import { WebFormEmbedOptions } from "../models/web-form-embed-options";

/**
 * Resolves and populates embed options for web forms from a combination of
 * JavaScript-provided configurations and HTML element (e.g. div) attributes.
 */
export class EmbedOptionsResolver {
    public static resolve(element: HTMLElement, embedOptions?: WebFormEmbedOptions): WebFormEmbedOptions {
        embedOptions ??= {};

        // The embed options on the tag are only used where they weren't passed in as embed options using javascript.
        // The javascript options takes precedence over tag attributes.
        embedOptions.title ??= element.getStringAttributeValueOrNull('data-title') ?? undefined;
        embedOptions.environment ??= element.getStringAttributeValueOrNull('data-environment') ?? undefined;
        embedOptions.formType ??= element.getStringAttributeValueOrNull('data-formType') ?? undefined;
        embedOptions.quoteId ??= element.getStringAttributeValueOrNull('data-quoteId') ?? undefined;
        embedOptions.policyId ??= element.getStringAttributeValueOrNull('data-policyId') ?? undefined;
        embedOptions.claimId ??= element.getStringAttributeValueOrNull('data-claimId') ?? undefined;
        embedOptions.portal ??= element.getStringAttributeValueOrNull('data-portal')
            ?? element.getStringAttributeValueOrNull('data-portal-id')
            ?? undefined;
        embedOptions.quoteType ??= element.getStringAttributeValueOrNull('data-quoteType') ?? undefined;
        embedOptions.stickyTopOffset
            ??= element.getStringAttributeValueOrNull('data-sticky-top-offset') ?? undefined;
        embedOptions.stickyBottomOffset
            ??= element.getStringAttributeValueOrNull('data-sticky-bottom-offset') ?? undefined;

        // backwards compatibility for the old sidebarOffset attribute:
        if (embedOptions.stickyTopOffset == null) {
            embedOptions.sidebarOffset ??=
                element.getStringAttributeValueOrNull('data-sidebar-offset') ?? undefined;
            embedOptions.stickyTopOffset = embedOptions.sidebarOffset;
        }

        embedOptions.mode ??= element.getStringAttributeValueOrNull('data-mode') ?? undefined;
        embedOptions.isTestData ??= element.getBooleanAttributeValue('data-istestdata', false);
        embedOptions.debug ??= element.getBooleanAttributeValue('data-debug', false);
        embedOptions.debugLevel ??= element.getNumberAttributeValueOrNull('data-debug-level') ?? undefined;
        embedOptions.autoResize ??= element.getBooleanAttributeValue('data-iframeresizer-enabled', true);
        embedOptions.productRelease ??= element.getStringAttributeValueOrNull('data-productRelease') ?? undefined;
        embedOptions.customerId ??= element.getStringAttributeValueOrNull('data-customerId') ?? undefined;
        const quoteVersionStr: string | null = element.getStringAttributeValueOrNull('data-version');
        if (quoteVersionStr) {
            embedOptions.quoteVersion = Number.parseInt(quoteVersionStr, 10);
        }
        embedOptions.tenant
            ??= element.getStringAttributeValueOrNull('data-tenant')
            ?? element.getStringAttributeValueOrNull('data-tenant-id')
            ?? undefined;
        embedOptions.product
            ??= element.getStringAttributeValueOrNull('data-product')
            ?? element.getStringAttributeValueOrNull('data-product-id')
            ?? undefined;
        if (embedOptions.tenant == null && embedOptions.product == 'DepositAssure-Concierge') {
            embedOptions.tenant = 'deposit-assure';
            embedOptions.product = 'concierge';
        }
        embedOptions.organisation ??= element.getStringAttributeValueOrNull('data-organisation') ?? undefined;
        embedOptions.portalOrganisation
            ??= element.getStringAttributeValueOrNull('data-portalOrganisation') ?? undefined;
        embedOptions.loaderBackground ??= element.getStringAttributeValueOrNull('data-loader-background') ?? undefined;
        embedOptions.accentColor1 ??= element.getStringAttributeValueOrNull('data-accent-color-1') ?? undefined;
        embedOptions.accentColor2 ??= element.getStringAttributeValueOrNull('data-accent-color-2') ?? undefined;
        embedOptions.accentColor3 ??= element.getStringAttributeValueOrNull('data-accent-color-3') ?? undefined;
        embedOptions.accentColor4 ??= element.getStringAttributeValueOrNull('data-accent-color-4') ?? undefined;
        embedOptions.modalPopup ??= element.getBooleanAttributeValue('data-modal-popup', false);
        embedOptions.modalZIndex ??= element.getNumberAttributeValueOrNull('data-modal-z-index') ?? undefined;
        embedOptions.minimumHeight ??= element.getStringAttributeValueOrNull('data-minimum-height') ?? undefined;
        embedOptions.scrollInsidePopup
            ??= element.getBooleanAttributeValueOrNull('data-scroll-inside-popup') ?? undefined;
        embedOptions.modalBackdrop ??= element.getBooleanAttributeValueOrNull('data-modal-backdrop') ?? undefined;
        embedOptions.width ??= element.getStringAttributeValueOrNull('data-width') ?? undefined;
        embedOptions.minimumWidth ??= element.getStringAttributeValueOrNull('data-minimum-width') ?? undefined;
        embedOptions.fullWidthBelowPixels
            ??= element.getNumberAttributeValueOrNull('data-full-width-below-pixels') ?? undefined;
        embedOptions.maximumWidth ??= element.getStringAttributeValueOrNull('data-maximum-width') ?? undefined;
        embedOptions.maximumHeight ??= element.getStringAttributeValueOrNull('data-maximum-height') ?? undefined;
        embedOptions.borderRadius ??= element.getStringAttributeValueOrNull('data-border-radius') ?? undefined;
        embedOptions.paddingXs ??= element.getStringAttributeValueOrNull('data-padding-xs') ?? undefined;
        embedOptions.paddingSm ??= element.getStringAttributeValueOrNull('data-padding-sm') ?? undefined;
        embedOptions.paddingMd ??= element.getStringAttributeValueOrNull('data-padding-md') ?? undefined;
        embedOptions.paddingLg ??= element.getStringAttributeValueOrNull('data-padding-lg') ?? undefined;
        embedOptions.paddingXl ??= element.getStringAttributeValueOrNull('data-padding-xl') ?? undefined;
        embedOptions.paddingXxl ??= element.getStringAttributeValueOrNull('data-padding-xxl') ?? undefined;
        embedOptions.margin ??= element.getStringAttributeValueOrNull('data-margin') ?? undefined;
        const seedFormDataJson: string | null = element.getStringAttributeValueOrNull('data-seed-form-data');
        if (embedOptions.seedFormData == null && seedFormDataJson) {
            embedOptions.seedFormData = JSON.parse(decodeURIComponent(seedFormDataJson));
        }
        const overwriteFormDataJson: string | null = element.getStringAttributeValueOrNull('data-overwrite-form-data');
        if (embedOptions.overwriteFormData == null && overwriteFormDataJson) {
            embedOptions.overwriteFormData = JSON.parse(decodeURIComponent(overwriteFormDataJson));
        }
        this.resolveSucceededUrlAndCallback(element, embedOptions);
        this.resolveAbortedUrlAndCallback(element, embedOptions);
        return embedOptions;
    }

    private static resolveSucceededUrlAndCallback(
        element: HTMLElement,
        embedOptions: WebFormEmbedOptions,
    ): void {
        const redirectUrl: string | undefined = this.getSucceededRedirectUrl(element, embedOptions);
        embedOptions.succeededRedirectUrl ??= redirectUrl;
        embedOptions.workflowCompletedRedirectUrl ??=
            element.getStringAttributeValueOrNull('data-workflow-completed-redirect-url') ?? undefined;
        const succeededCallbackFuncName: string | null =
            element.getStringAttributeValueOrNull('data-succeeded-callback');
        if (succeededCallbackFuncName) {
            this.showDeprecatedWarning("data attribute 'data-succeeded-callback'",
                "add an event listener to the 'workflow-completed' event");
        }
        if (embedOptions.succeededCallback != null) {
            this.showDeprecatedWarning("callback 'succeededCallback'",
                "add an event listener to the 'workflow-completed' event");
        } else if (succeededCallbackFuncName) {
            embedOptions.succeededCallback = (<any>window)[succeededCallbackFuncName];
        }
    }

    private static getSucceededRedirectUrl(
        element: HTMLElement,
        embedOptions: WebFormEmbedOptions,
    ): string | undefined {
        const succeededUrlAttribute: string | undefined =
            element.getStringAttributeValueOrNull('data-succeeded-redirect-url') ?? undefined;
        if (succeededUrlAttribute) {
            this.showDeprecatedWarning("data attribute 'data-succeeded-redirect-url'",
                "use 'data-workflow-completed-redirect-url' attribute");
        }
        if (embedOptions.succeededRedirectUrl) {
            this.showDeprecatedWarning("embed option 'succeededRedirectUrl'",
                "use 'workflowCompletedRedirectUrl' option");
        }
        return succeededUrlAttribute;
    }

    private static resolveAbortedUrlAndCallback(
        element: HTMLElement,
        embedOptions: WebFormEmbedOptions,
    ): void {
        const redirectUrl: string | undefined = this.getAbortedRedirectUrl(element, embedOptions);
        embedOptions.abortedRedirectUrl ??= redirectUrl;
        embedOptions.workflowAbortedRedirectUrl ??=
            element.getStringAttributeValueOrNull('data-workflow-aborted-redirect-url') ?? undefined;
        const abortedCallbackFuncName: string | null = element.getStringAttributeValueOrNull('data-aborted-callback');
        if (abortedCallbackFuncName) {
            this.showDeprecatedWarning("data attribute 'data-aborted-callback'",
                "add an event listener to the 'workflow-aborted' event");
        }
        if (embedOptions.abortedCallback != null) {
            this.showDeprecatedWarning("callback 'abortedCallback'",
                "add an event listener to the 'workflow-aborted' event");
        } else if (abortedCallbackFuncName) {
            embedOptions.abortedCallback = (<any>window)[abortedCallbackFuncName];
        }
    }

    private static getAbortedRedirectUrl(
        element: HTMLElement,
        embedOptions: WebFormEmbedOptions,
    ): string | undefined {
        const abortedUrlAttribute: string | undefined =
            element.getStringAttributeValueOrNull('data-aborted-redirect-url') ?? undefined;
        if (abortedUrlAttribute) {
            this.showDeprecatedWarning("data attribute 'data-aborted-redirect-url'",
                "use 'data-workflow-aborted-redirect-url' attribute");
        }
        if (embedOptions.abortedRedirectUrl) {
            this.showDeprecatedWarning("embed option 'abortedRedirectUrl'",
                "use 'workflowAbortedRedirectUrl' option");
        }
        return abortedUrlAttribute;
    }

    private static showDeprecatedWarning(deprecatedSetting: string, instruction: string): void {
        const message: string = `The ${deprecatedSetting} has been deprecated `
            + "and will be removed in a future version. "
            + `Please ${instruction} instead.`;
        console.warn(message);
    }
}
